import React, { useCallback, useState } from "react";
import { usePopper } from "react-popper";
import { commitMutation, graphql, useFragment } from "react-relay";

import { faEllipsisV, faPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faPencil, faTheaterMasks, faTrashCan } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import moment, { type Moment } from "moment";

import { activateDelegation } from "~/lib/delegation";
import useMedia from "~/lib/hooks/useMedia";
import { environment } from "~/models";
import { Button, toast, Tooltip, useModals } from "~/ui";
import type { UserAttrs } from "~/ui/legacy/Avatar";
import Avatar from "~/ui/legacy/Avatar";
import { HelpCenterLink, IconButton, Modal } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import { ExpirySelect } from "./utils";

import type { DelegateCard_Delegation$key } from "~/__generated__/DelegateCard_Delegation.graphql";
import type { DelegateCardEditMutation as DelegateCardEditMutationType } from "~/__generated__/DelegateCardEditMutation.graphql";
import type { DelegateCardRemoveDelegateMutation as DelegateCardRemoveDelegateMutationType } from "~/__generated__/DelegateCardRemoveDelegateMutation.graphql";
import type { DelegateCardRemoveDelegatorMutation as DelegateCardRemoveDelegatorMutationType } from "~/__generated__/DelegateCardRemoveDelegatorMutation.graphql";

import styles from "./css/DelegateCard.module.css";
import modalStyles from "./css/ModalStyles.module.css";

const removeDelegateMutation = graphql`
	mutation DelegateCardRemoveDelegateMutation($delegateId: ID!) {
		deleteDelegate(input: { delegateId: $delegateId }) {
			ok
			error
			delegateId @deleteRecord
		}
	}
`;

const removeDelegatorMutation = graphql`
	mutation DelegateCardRemoveDelegatorMutation($delegatorID: ID!) {
		deleteDelegator(input: { delegatorId: $delegatorID }) {
			ok
			error
			delegatorId @deleteRecord
		}
	}
`;

const editDelegateMutation = graphql`
	mutation DelegateCardEditMutation($delegateId: ID!, $expiryNew: DateTime!) {
		editDelegate(input: { delegateId: $delegateId, expiryNew: $expiryNew }) {
			ok
			error
			delegate {
				id
				expiryDate
			}
		}
	}
`;

type UserInfoType = {
	id: string;
	fullName: string;
	email: string;
	image: string;
};

type DelegateCardProps = {
	isDelegate: boolean;
	delegation: DelegateCard_Delegation$key;
};

type RemoveDelegateType = {
	delegateId: string;
	user: UserInfoType;
	isDelegate: boolean;
	isExpired: boolean;
	onClose: () => void;
};

type EditDelegateType = {
	delegateId: string;
	user: UserInfoType;
	currentExpiry: Moment | null;
	onClose: () => void;
};

function EditModal({ delegateId, currentExpiry, user, onClose }: EditDelegateType) {
	const [newExpiry, setNewExpiry] = useState<Moment | null>(currentExpiry);
	const [isDatePickerOpen, setIsDatePickerOpen] = React.useState(false);

	const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
	const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
	const { styles, attributes } = usePopper(referenceElement, popperElement, {
		placement: "bottom-end",
	});

	const removeModal = useModals();

	const showRemoveModal = React.useCallback(() => {
		onClose();
		removeModal.open(({ close }) => (
			<RemoveModal
				isDelegate={true}
				delegateId={delegateId}
				user={user}
				onClose={close}
				isExpired={moment().isBefore(newExpiry) ? false : true}
			/>
		));
	}, [removeModal, newExpiry, delegateId, user, onClose]);

	const updateExpiryDate = React.useCallback((newDate: moment.Moment) => {
		setNewExpiry(newDate.endOf("day"));
		setIsDatePickerOpen(false);
	}, []);

	const showDatePicker = React.useCallback(() => {
		setIsDatePickerOpen(true);
	}, [setIsDatePickerOpen]);

	const closeDatePicker = React.useCallback(() => {
		setIsDatePickerOpen(false);
	}, [setIsDatePickerOpen]);

	const editDelegate = React.useCallback(() => {
		commitMutation<DelegateCardEditMutationType>(environment, {
			mutation: editDelegateMutation,
			variables: {
				delegateId,
				expiryNew: newExpiry ? newExpiry.format() : moment().add(3000, "years").format(),
			},
			onCompleted: data => {
				if (data.editDelegate) {
					if (data.editDelegate.ok) {
						toast.success(`${user.fullName} (${user.email}) expiry date has successfully been updated.`);
					} else if (data.editDelegate.error) {
						toast.error(`Failed to edit the expiry for ${user.fullName}.`);
					}
				}
				onClose();
			},
		});
	}, [delegateId, newExpiry, onClose, user]);

	const removeExpiry = React.useCallback(() => {
		setIsDatePickerOpen(false);
		setNewExpiry(null);
	}, []);

	return (
		<Modal onClose={onClose} width="37.25rem">
			<Modal.Header onClose={onClose}>
				<Modal.Title>Edit Delegate</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className={modalStyles.bodyHeading}>
					The user has access to managing your account on your behalf. <strong>{user.fullName}</strong> ({user.email}).
				</div>
				<div className={modalStyles.infoBox}>
					<div className={modalStyles.infoRow}>
						<div className={modalStyles.title}>Delegate:</div>
						<div className={modalStyles.info}>
							<div className={modalStyles.subject}>
								<Avatar user={user} width="1.5em" link={false} />
								<span className={modalStyles.name}>{user.fullName}</span>
							</div>
						</div>
					</div>
				</div>

				<div>
					<div className={modalStyles.datePickerContainer} onClick={showDatePicker} ref={setReferenceElement}>
						<div className={modalStyles.text}>{newExpiry ? newExpiry.format("ll") : "No Expiry"}</div>
						<div className={modalStyles.editDate}>Edit expiry date</div>
					</div>
					{isDatePickerOpen && (
						<div ref={setPopperElement} style={styles.popper} {...attributes.popper}>
							<ExpirySelect
								date={newExpiry ? newExpiry : undefined}
								updateDate={updateExpiryDate}
								removeDate={removeExpiry}
								onClose={closeDatePicker}
							/>
						</div>
					)}
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" variant="destructive" onClick={showRemoveModal}>
					Remove Delegate
				</Button>
				<Button size="normal" variant="primary" onClick={editDelegate}>
					Save Changes
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

function RemoveModal({ delegateId, user, onClose, isDelegate, isExpired }: RemoveDelegateType) {
	const removeDelegate = React.useCallback(() => {
		commitMutation<DelegateCardRemoveDelegateMutationType>(environment, {
			mutation: removeDelegateMutation,
			variables: {
				delegateId,
			},
			onCompleted: data => {
				if (data.deleteDelegate) {
					if (data.deleteDelegate.ok) {
						toast.success(`${user.fullName} has been removed as an account delegate.`);
					} else {
						toast.error(`Failed to remove ${user.fullName} as a delegate.`);
					}
				}
				onClose();
			},
		});
	}, [delegateId, user, onClose]);

	const removeDelegator = React.useCallback(() => {
		commitMutation<DelegateCardRemoveDelegatorMutationType>(environment, {
			mutation: removeDelegatorMutation,
			variables: {
				delegatorID: delegateId,
			},
			onCompleted: data => {
				if (data.deleteDelegator) {
					if (data.deleteDelegator.ok) {
						toast.success(`You have removed yourself as a delegate to`);
					} else {
						toast.error(`Failed to remove you as a delegate`);
					}
				}
				onClose();
			},
		});
	}, [delegateId, onClose]);

	const heading = isDelegate ? "Confirm delegate removal" : "Remove yourself as delegate";

	return (
		<Modal onClose={onClose}>
			<Modal.Header onClose={onClose}>
				<Modal.Title>{heading}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className={modalStyles.bodyHeading}>
					{isDelegate ? (
						<>Please confirm that you wish to remove delegate privileges from the following user:</>
					) : (
						<>
							Please confirm that you wish to remove yourself as a delegate user to the following account,{" "}
							<strong>{user.fullName}</strong> ({user.email}).
						</>
					)}
				</div>

				<div className={modalStyles.infoBox}>
					<div className={modalStyles.infoRow}>
						<div className={modalStyles.title}>Delegate:</div>
						<div className={modalStyles.info}>
							<div className={modalStyles.subject}>
								<Avatar user={user as UserAttrs} width="1.5em" link={false} />
								<span className={modalStyles.name}>{user.fullName}</span>
							</div>
						</div>
					</div>
				</div>

				<div className={modalStyles.bodyParagraph}>
					{!isExpired && (
						<>
							{isDelegate ? (
								<>
									By removing <strong>{user.fullName}</strong> as a delegate user, they will no longer have access to
									manage your account.{" "}
								</>
							) : (
								<>
									By removing yourself as a delegate user, you will no longer have access to managing their account on
									their behalf. <HelpCenterLink articleId="5887349">Learn more about Delegated access</HelpCenterLink>.
								</>
							)}
						</>
					)}
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" onClick={onClose}>
					Cancel
				</Button>
				<Button size="normal" variant="primary" onClick={isDelegate ? removeDelegate : removeDelegator}>
					Remove
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

export function AddDelegateCard({ addDelegateCallback }: { addDelegateCallback: () => void }) {
	const noHover = useMedia("(hover: none)");
	return (
		<Tooltip content="Add a Delegate" placement="bottom" touch={false}>
			<div onClick={addDelegateCallback} className={cx(styles.card, styles.addDelegateCard)}>
				<div className={styles.addIcon}>
					<FontAwesomeIcon className={styles.icon} icon={faPlus} />
				</div>
				{noHover && <span>&ensp;Add a delegate</span>}
			</div>
		</Tooltip>
	);
}

export default function DelegateCard({ isDelegate, delegation: delegationProp }: DelegateCardProps) {
	const delegation = useFragment<DelegateCard_Delegation$key>(
		graphql`
			fragment DelegateCard_Delegation on Delegation {
				id
				expiryDate
				lastAccessed
				createdAt
				delegator {
					id
					fullName
					email
					image
				}
				delegate {
					id
					fullName
					email
					image
				}
			}
		`,
		delegationProp,
	);

	const { id: delegateId, expiryDate: expiry, createdAt, lastAccessed } = delegation;
	const user = isDelegate ? delegation.delegate : delegation.delegator;
	const expiryFormatted = React.useMemo(() => (expiry ? moment(expiry).format("ll") : "Never"), [expiry]);
	const lastAccessedFormatted = React.useMemo(
		() => (lastAccessed ? moment(lastAccessed).format("ll") : "Never"),
		[lastAccessed],
	);
	const createdAtFormatted = React.useMemo(() => (createdAt ? moment(createdAt).format("ll") : "Unknown"), [createdAt]);
	const delegateExpired = React.useMemo(() => (expiry && !moment().isBefore(expiry) ? true : false), [expiry]);

	const [kebabMenuOpen, setKebabMenuOpen] = React.useState(false);

	const modalContext = useModals();

	const showRemoveModal = React.useCallback(() => {
		modalContext.open(({ close }) => (
			<RemoveModal
				isDelegate={isDelegate ? true : false}
				delegateId={delegateId}
				user={user}
				onClose={close}
				isExpired={delegateExpired}
			/>
		));
	}, [modalContext, delegateExpired, delegateId, user, isDelegate]);

	const showEditModal = React.useCallback(() => {
		modalContext.open(({ close }) => (
			<EditModal currentExpiry={expiry ? moment(expiry) : null} delegateId={delegateId} user={user} onClose={close} />
		));
	}, [modalContext, delegateId, user, expiry]);

	const activateDelegate = useCallback(() => {
		if (user.id) activateDelegation(user.id);
	}, [user]);

	return (
		<>
			<div className={cx(styles.card, { [styles.expired]: delegateExpired })}>
				<div className={cx(styles.header)}>
					<div className={styles.userInfo}>
						<Avatar user={user as UserAttrs} link={false} width="2em" />
						<div>
							<div className={styles.userName}>{user.fullName}</div>
							<div className={styles.emailAddress}>{user.email}</div>
						</div>
					</div>

					<Menu mobileHeaderTitle="Integration actions" open={kebabMenuOpen} onOpenChange={setKebabMenuOpen}>
						<Menu.Trigger asChild={true}>
							<IconButton className={styles.kebabMenu} icon={faEllipsisV} />
						</Menu.Trigger>
						<Menu.Content>
							{isDelegate ? (
								<Menu.Item onSelect={showEditModal} icon={faPencil}>
									Edit access…
								</Menu.Item>
							) : (
								!delegateExpired && (
									<Menu.Item onSelect={activateDelegate} icon={faTheaterMasks}>
										Log in as…
									</Menu.Item>
								)
							)}
							<Menu.Item onSelect={showRemoveModal} icon={faTrashCan}>
								Remove access
							</Menu.Item>
						</Menu.Content>
					</Menu>
				</div>
				<div className={styles.dateInfoContainer}>
					<p className={styles.dateInfo}>Date granted: {createdAtFormatted}</p>
					<p className={styles.dateInfo}>Last Accessed: {lastAccessedFormatted}</p>
				</div>
				<div>
					<p>
						<span className={cx(styles.badge, { [styles.expired]: delegateExpired })}>
							{delegateExpired ? "Expired: " : "Expires: "} {expiryFormatted}
						</span>
					</p>
				</div>
			</div>
		</>
	);
}
